import { storageHelpers } from '../../../common/helpers/browser-storage';
import { SESSIONS_INCIDENT_IDS } from '../constants';
import type { Discipline } from './pro-qa-buttons-helpers';

const TEMPORARY_INCIDENT_IDS_BY_PANEL = 'temporaryIncidentIdsByPanel';
const DRAFT_STORAGE_KEY_PREFIX = 'panel';
const SAVED_EVENT_STORAGE_KEY_PREFIX = 'event';

const EMPTY_INCIDENT_IDS: Record<Discipline, string | undefined> = {
    POLICE: undefined,
    FIRE: undefined,
    EMS: undefined,
};

type TemporaryIncidentIdsByPanel = Record<string, Partial<Record<Discipline, string | undefined>>>;
type TemporaryIncidentIds = Record<Discipline, string | undefined>;

export type ProQaTempIncidentStorageKeys = {
    draftStorageKey?: string;
    savedEventStorageKey?: string;
    activeStorageKey?: string;
};

function normalizeStorageKey(
    workspacePanelStorageKey: string | number | undefined
): string | undefined {
    if (workspacePanelStorageKey === undefined || workspacePanelStorageKey === null) {
        return undefined;
    }

    const normalizedStorageKey = String(workspacePanelStorageKey).trim();
    return normalizedStorageKey || undefined;
}

function buildScopedStorageKey(
    storageKeyPrefix: string,
    storageKey: string | number | undefined
): string | undefined {
    const normalizedStorageKey = normalizeStorageKey(storageKey);

    return normalizedStorageKey ? `${storageKeyPrefix}:${normalizedStorageKey}` : undefined;
}

function hasAnyTemporaryIncidentIds(temporaryIncidentIds: TemporaryIncidentIds): boolean {
    return Object.values(temporaryIncidentIds).some((value) => !!value?.trim());
}

function mergeTemporaryIncidentIds(
    ...temporaryIncidentIdsList: Array<Partial<TemporaryIncidentIds> | undefined>
): TemporaryIncidentIds {
    return (Object.keys(EMPTY_INCIDENT_IDS) as Discipline[]).reduce<TemporaryIncidentIds>(
        (accumulator, discipline) => {
            const incidentId = temporaryIncidentIdsList
                .map((temporaryIncidentIds) => temporaryIncidentIds?.[discipline]?.trim())
                .find((value): value is string => !!value);

            accumulator[discipline] = incidentId;
            return accumulator;
        },
        { ...EMPTY_INCIDENT_IDS }
    );
}

function getAllTemporaryIncidentIdsByPanel(): TemporaryIncidentIdsByPanel {
    return (
        storageHelpers.getSessionItem<TemporaryIncidentIdsByPanel>(
            TEMPORARY_INCIDENT_IDS_BY_PANEL
        ) ?? {}
    );
}

function persistAllTemporaryIncidentIdsByPanel(
    allIncidentIdsByPanel: TemporaryIncidentIdsByPanel
): void {
    if (Object.keys(allIncidentIdsByPanel).length === 0) {
        storageHelpers.removeSessionItem(TEMPORARY_INCIDENT_IDS_BY_PANEL);
        return;
    }

    storageHelpers.setSessionItem(TEMPORARY_INCIDENT_IDS_BY_PANEL, allIncidentIdsByPanel);
}

function getLegacyTemporaryIncidentIds(): TemporaryIncidentIds | undefined {
    const legacyTemporaryIncidentIds = mergeTemporaryIncidentIds(
        storageHelpers.getSessionItem<Partial<TemporaryIncidentIds>>(SESSIONS_INCIDENT_IDS) ??
            undefined
    );

    return hasAnyTemporaryIncidentIds(legacyTemporaryIncidentIds)
        ? legacyTemporaryIncidentIds
        : undefined;
}

export function getProQaTempIncidentStorageKeys({
    workspacePanelInitialId,
    eventId,
}: {
    workspacePanelInitialId?: string | number;
    eventId?: string | number;
}): ProQaTempIncidentStorageKeys {
    // Draft sessions need panel-scoped isolation. Once the event has a real id, temp ProQA ids
    // must follow the saved event so reload/reopen flows can still recover and overwrite them.
    const draftStorageKey = buildScopedStorageKey(
        DRAFT_STORAGE_KEY_PREFIX,
        workspacePanelInitialId
    );
    const savedEventStorageKey = buildScopedStorageKey(SAVED_EVENT_STORAGE_KEY_PREFIX, eventId);

    return {
        draftStorageKey,
        savedEventStorageKey,
        activeStorageKey: savedEventStorageKey ?? draftStorageKey,
    };
}

export function getPanelTemporaryIncidentIds(
    workspacePanelStorageKey?: string | number
): TemporaryIncidentIds {
    const normalizedStorageKey = normalizeStorageKey(workspacePanelStorageKey);

    if (!normalizedStorageKey) {
        return { ...EMPTY_INCIDENT_IDS };
    }

    return {
        ...EMPTY_INCIDENT_IDS,
        ...getAllTemporaryIncidentIdsByPanel()[normalizedStorageKey],
    };
}

export function setPanelTemporaryIncidentIds(
    workspacePanelStorageKey: string | number | undefined,
    temporaryIncidentIds: Partial<TemporaryIncidentIds>
): TemporaryIncidentIds {
    const normalizedStorageKey = normalizeStorageKey(workspacePanelStorageKey);
    const nextPanelIncidentIds = mergeTemporaryIncidentIds(temporaryIncidentIds);

    if (!normalizedStorageKey) {
        return nextPanelIncidentIds;
    }

    const allIncidentIdsByPanel = getAllTemporaryIncidentIdsByPanel();

    if (hasAnyTemporaryIncidentIds(nextPanelIncidentIds)) {
        allIncidentIdsByPanel[normalizedStorageKey] = nextPanelIncidentIds;
    } else {
        delete allIncidentIdsByPanel[normalizedStorageKey];
    }

    persistAllTemporaryIncidentIdsByPanel(allIncidentIdsByPanel);

    return nextPanelIncidentIds;
}

export function setPanelTemporaryIncidentId(
    workspacePanelStorageKey: string | number | undefined,
    discipline: Discipline,
    incidentId: string | undefined
): TemporaryIncidentIds {
    const normalizedStorageKey = normalizeStorageKey(workspacePanelStorageKey);
    const currentPanelIncidentIds = getPanelTemporaryIncidentIds(normalizedStorageKey);

    if (!normalizedStorageKey) {
        return currentPanelIncidentIds;
    }

    const nextPanelIncidentIds = {
        ...currentPanelIncidentIds,
        [discipline]: incidentId,
    };

    return setPanelTemporaryIncidentIds(normalizedStorageKey, nextPanelIncidentIds);
}

/**
 * Resolves temporary ProQA incident ids for the current panel
 *
 * Resolution order:
 * 1. preferred in-memory ids from the caller
 * 2. the current scoped storage bucket (`event:<id>` or `panel:<id>`)
 * 3. the draft bucket as a fallback during draft -> saved-event transitions
 * 4. the legacy global key (`temporaryIncidentIds`) only if scoped storage is empty
 *
 * If legacy data is found, it is migrated into the current scoped bucket and the legacy key is removed
 */
export function getResolvedTemporaryIncidentIds(
    storageKeys: ProQaTempIncidentStorageKeys,
    preferredTemporaryIncidentIds?: Partial<TemporaryIncidentIds>
): TemporaryIncidentIds {
    const resolvedTemporaryIncidentIds = mergeTemporaryIncidentIds(
        preferredTemporaryIncidentIds,
        getPanelTemporaryIncidentIds(storageKeys.activeStorageKey),
        storageKeys.savedEventStorageKey && storageKeys.draftStorageKey
            ? getPanelTemporaryIncidentIds(storageKeys.draftStorageKey)
            : undefined
    );

    if (hasAnyTemporaryIncidentIds(resolvedTemporaryIncidentIds)) {
        return resolvedTemporaryIncidentIds;
    }

    const legacyTemporaryIncidentIds = getLegacyTemporaryIncidentIds();
    if (!legacyTemporaryIncidentIds) {
        return resolvedTemporaryIncidentIds;
    }

    if (storageKeys.activeStorageKey) {
        setPanelTemporaryIncidentIds(storageKeys.activeStorageKey, legacyTemporaryIncidentIds);
        storageHelpers.removeSessionItem(SESSIONS_INCIDENT_IDS);
    }

    return legacyTemporaryIncidentIds;
}

export function setResolvedTemporaryIncidentId(
    storageKeys: ProQaTempIncidentStorageKeys,
    discipline: Discipline,
    incidentId: string | undefined
): TemporaryIncidentIds {
    const nextPanelIncidentIds = setPanelTemporaryIncidentId(
        storageKeys.activeStorageKey,
        discipline,
        incidentId
    );

    if (
        storageKeys.savedEventStorageKey &&
        storageKeys.draftStorageKey &&
        storageKeys.savedEventStorageKey !== storageKeys.draftStorageKey
    ) {
        setPanelTemporaryIncidentId(storageKeys.draftStorageKey, discipline, undefined);
    }

    return nextPanelIncidentIds;
}

export function migrateTemporaryIncidentIdsToSavedEvent(
    storageKeys: ProQaTempIncidentStorageKeys,
    preferredTemporaryIncidentIds?: Partial<TemporaryIncidentIds>
): TemporaryIncidentIds {
    if (
        !storageKeys.savedEventStorageKey ||
        !storageKeys.draftStorageKey ||
        storageKeys.savedEventStorageKey === storageKeys.draftStorageKey
    ) {
        return getResolvedTemporaryIncidentIds(storageKeys, preferredTemporaryIncidentIds);
    }

    const nextPanelIncidentIds = mergeTemporaryIncidentIds(
        preferredTemporaryIncidentIds,
        getPanelTemporaryIncidentIds(storageKeys.savedEventStorageKey),
        getPanelTemporaryIncidentIds(storageKeys.draftStorageKey)
    );

    setPanelTemporaryIncidentIds(storageKeys.savedEventStorageKey, nextPanelIncidentIds);
    setPanelTemporaryIncidentIds(storageKeys.draftStorageKey, EMPTY_INCIDENT_IDS);

    return nextPanelIncidentIds;
}
